import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Button from '../../components/elements/button';
import { Search } from '../../assets/icons';
import { ordersApi } from '../../network/apis/orders';
import '../../assets/css/pages/orders.css';

const Orders = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [filters, setFilters] = useState({
        status: 'all',
        timeframe: 'all'
    });
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchOrders = useCallback(async () => {
        try {
            setLoading(true);
            const params = {};
            if (filters.status !== 'all') params.status = filters.status;
            if (filters.timeframe !== 'all') {
                const now = new Date();
                let startDate;
                switch (filters.timeframe) {
                    case 'last-month':
                        startDate = new Date(now.setMonth(now.getMonth() - 1));
                        break;
                    case 'last-3months':
                        startDate = new Date(now.setMonth(now.getMonth() - 3));
                        break;
                    case 'last-6months':
                        startDate = new Date(now.setMonth(now.getMonth() - 6));
                        break;
                    case 'last-year':
                        startDate = new Date(now.setFullYear(now.getFullYear() - 1));
                        break;
                    default:
                        break;
                }
                if (startDate) params.start_date = startDate.toISOString().split('T')[0];
            }
            if (searchTerm) params.search = searchTerm;

            const response = await ordersApi.getOrders(params);
            setOrders(response?.results || response?.data || []);
        } catch (error) {
            console.error('Error fetching orders:', error);
        } finally {
            setLoading(false);
        }
    }, [filters, searchTerm]);

    useEffect(() => {
        fetchOrders();
    }, [fetchOrders]);

    const handleCancelOrder = async (orderId) => {
        if (!window.confirm('Are you sure you want to cancel this order?')) return;
        try {
            await ordersApi.cancelOrder(orderId);
            fetchOrders();
        } catch (error) {
            alert('Failed to cancel order');
        }
    };

    const getStatusIcon = (status) => {
        switch (status?.toUpperCase()) {
            case 'PENDING':
                return '⏳';
            case 'PROCESSING':
                return '📦';
            case 'SHIPPED':
                return '🚚';
            case 'DELIVERED':
                return '✅';
            case 'CANCELLED':
                return '❌';
            default:
                return '📋';
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-NG', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    return (
        <div className="orders-page">
            <div className="page-header">
                <div className="header-content">
                    <h1>My Orders</h1>
                    <p>{orders.length} order{orders.length !== 1 ? 's' : ''} placed</p>
                </div>
            </div>

            <div className="filters-section">
                <div className="search-bar">
                    <img src={Search} alt="" />
                    <input
                        type="text"
                        placeholder="Search orders by ID or product..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && fetchOrders()}
                    />
                </div>

                <div className="filters">
                    <select
                        value={filters.status}
                        onChange={(e) => setFilters({...filters, status: e.target.value})}
                    >
                        <option value="all">All Status</option>
                        <option value="pending">Pending</option>
                        <option value="processing">Processing</option>
                        <option value="shipped">Shipped</option>
                        <option value="delivered">Delivered</option>
                        <option value="cancelled">Cancelled</option>
                    </select>

                    <select
                        value={filters.timeframe}
                        onChange={(e) => setFilters({...filters, timeframe: e.target.value})}
                    >
                        <option value="all">All Time</option>
                        <option value="last-month">Last Month</option>
                        <option value="last-3months">Last 3 Months</option>
                        <option value="last-6months">Last 6 Months</option>
                        <option value="last-year">Last Year</option>
                    </select>
                </div>
            </div>

            {loading ? (
                <div className="orders-loading">
                    <div className="loading-spinner"></div>
                    <p>Loading your orders...</p>
                </div>
            ) : orders.length === 0 ? (
                <div className="orders-empty">
                    <div className="empty-icon">📦</div>
                    <h2>No Orders Yet</h2>
                    <p>You haven't placed any orders yet. Start shopping!</p>
                    <Link to="/store">
                        <Button text="Browse Products" variant="primary" />
                    </Link>
                </div>
            ) : (
                <div className="orders-list">
                    {orders.map(order => (
                        <div key={order.id} className="order-card">
                            <div className="order-header">
                                <div className="order-meta">
                                    <span className="order-id">Order #{order.order_number || order.id}</span>
                                    <span className="order-date">{formatDate(order.created_at || order.date)}</span>
                                </div>
                                <span className={`status ${order.status?.toLowerCase()}`}>
                                    {getStatusIcon(order.status)} {order.status?.charAt(0) + order.status?.slice(1).toLowerCase()}
                                </span>
                            </div>

                            <div className="order-items">
                                {(order.items || []).slice(0, 3).map((item, index) => (
                                    <div key={index} className="order-item">
                                        <img 
                                            src={item.product_image || item.product?.image || item.image || '/placeholder.png'} 
                                            alt={item.product_name || item.product?.name || item.name} 
                                        />
                                        <div className="item-details">
                                            <h3>{item.product_name || item.product?.name || item.name}</h3>
                                            <div className="item-meta">
                                                <span>Qty: {item.quantity}</span>
                                                <span>₦{parseFloat(item.price || item.unit_price || 0).toLocaleString()}</span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                {order.items?.length > 3 && (
                                    <div className="more-items">
                                        +{order.items.length - 3} more item{order.items.length - 3 > 1 ? 's' : ''}
                                    </div>
                                )}
                            </div>

                            <div className="order-summary">
                                <div className="summary-group">
                                    <label>Total Amount</label>
                                    <span className="total">₦{parseFloat(order.total || order.total_amount || 0).toLocaleString()}</span>
                                </div>
                                <div className="summary-group">
                                    <label>Payment Status</label>
                                    <span className={`payment-status ${(order.payment_status || order.paymentStatus)?.toLowerCase()}`}>
                                        {order.payment_status || order.paymentStatus}
                                    </span>
                                </div>
                                {(order.shipment?.tracking_number || order.trackingNumber) && (
                                    <div className="summary-group">
                                        <label>Tracking Number</label>
                                        <span className="tracking">{order.shipment?.tracking_number || order.trackingNumber}</span>
                                    </div>
                                )}
                            </div>

                            <div className="order-actions">
                                <Link to={`/store/orders/${order.id}`}>
                                    <Button
                                        text="View Details"
                                        variant="outline"
                                    />
                                </Link>
                                {(order.status === 'PENDING' || order.status === 'PROCESSING') && (
                                    <Button
                                        text="Cancel Order"
                                        variant="error"
                                        onClick={() => handleCancelOrder(order.id)}
                                    />
                                )}
                                {order.status === 'DELIVERED' && (
                                    <Link to={`/store/orders/${order.id}/review`}>
                                        <Button
                                            text="Write Review"
                                            variant="primary"
                                        />
                                    </Link>
                                )}
                                {(order.status === 'SHIPPED' || order.status === 'PROCESSING') && order.shipment && (
                                    <Link to={`/store/orders/${order.id}/track`}>
                                        <Button
                                            text="Track Order"
                                            variant="primary"
                                        />
                                    </Link>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default Orders;